import { css } from '@emotion/react';
import tw from 'twin.macro';
import {
  backgroundColors,
  borderColors,
  textColors,
} from '@teetsh/app/src/styles/themeConstants';

export const specialInsertStyles = css`
  // Sinon le fill du chevron est set par froala, donc on override
  #chevron-icon {
    ${tw`w-4! h-4! transition-transform duration-200`}
    path {
      fill: transparent !important;
    }
  }

  /* Insert special box button styling */
  .fr-toolbar .fr-command[data-cmd='insertSpecialBox'] {
    ${tw`rounded-lg! h-8! mt-2! mb-1.5! pr-[5px]! pl-3! pt-1.5! pb-[5px]!`}
    border: 1px solid ${borderColors['neutral-moderate']} !important;

    span {
      ${tw`p-0!`}
    }

    &:hover {
      background-color: ${backgroundColors['neutral-moderate']} !important;
    }

    &.fr-active {
      background-color: #e9edf5 !important;
      border-color: #5b6b8c !important;

      #chevron-icon {
        ${tw`rotate-180`}
      }
    }
  }

  /* Special insert boxes styles */
  .special-insert {
    ${tw`relative my-2 flex items-start rounded p-3 pl-4 pr-5 gap-2 border-l-[3px] border-solid`}

    /* Close button - always visible */
    .special-insert-close-btn {
      ${tw`absolute top-2 right-2 p-1 rounded cursor-pointer border-0 bg-transparent`}
      color: ${textColors['neutral-moderate']};
      z-index: 10;
      line-height: 0; /* Prevent line-height issues */
      font-size: 0; /* Hide any text content like &nbsp; */

      svg {
        ${tw`w-4 h-4 block`}/* Block display to prevent inline spacing issues */
      }
    }

    /* Icon container - direct child of special-insert */
    .special-insert-box-icon {
      ${tw`flex-shrink-0 flex items-center justify-center w-6 h-6`}

      svg {
        ${tw`w-full h-full`}
      }
    }

    /* Main content container - contains header and content */
    .special-insert-main {
      ${tw`flex-1 min-w-0`}
    }

    /* Header with title */
    .special-insert-header {
      ${tw`mb-1 block w-full`}

      .special-insert-title {
        ${tw`font-semibold inline-block select-none`}
      }
    }

    /* Ensure content inside special inserts is editable */
    .special-insert-content {
      ${tw`min-h-[1.5rem]`}

      /* Reset paragraph margins inside special inserts */
      p {
        ${tw`m-0`}

        &:not(:last-child) {
          ${tw`mb-2`}
        }
      }

      /* Ensure lists work properly inside special inserts */
      ul,
      ol {
        ${tw`ml-6`}
      }
    }
  }

  /* Responsive styles for mobile */
  @media (max-width: 640px) {
    .special-insert {
      ${tw`p-2! px-3! my-1.5! gap-1.5!`}

      .special-insert-box-icon {
        ${tw`w-5! h-5!`}
      }

      .special-insert-header strong {
        ${tw`text-sm`}
      }

      .special-insert-content {
        ${tw`text-sm`}
      }
    }
  }

  /* Responsive styles for tablet */
  @media (min-width: 641px) and (max-width: 1024px) {
    .special-insert {
      ${tw`py-2.5! pr-4! pl-3.5!`}
    }
  }

  /* Print styles for special inserts */
  @media print {
    .special-insert {
      ${tw`break-inside-avoid border-l-[2px]!`}
    }
  }

  /* Custom dropdown styles for special inserts */
  .special-inserts-dropdown {
    ${tw`bg-white rounded-lg shadow-lg overflow-hidden min-w-[280px] max-w-[320px] border`}
    border-color: ${borderColors['neutral-quiet']};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }

  .special-inserts-header {
    ${tw`flex items-center justify-between px-4 py-3 border-b`}
    border-color: ${borderColors['neutral-quiet']};

    span {
      ${tw`text-base font-semibold`}
      color: ${textColors['neutral-quiet']};
    }
  }

  .special-inserts-close {
    ${tw`p-1.5 rounded hover:bg-gray-100 transition-colors cursor-pointer border-0 bg-transparent`}
    color: ${textColors['neutral-quiet']};

    &:hover {
      color: ${textColors['neutral-moderate']};
    }

    svg {
      ${tw`w-5 h-5`}
    }
  }

  .special-inserts-divider {
    ${tw`h-px mx-4`}
    background-color: ${borderColors['neutral-quiet']};
  }

  .special-inserts-list {
    ${tw`py-0 font-semibold`}
  }

  .special-insert-item {
    ${tw`flex items-center justify-between gap-2.5 px-4 py-2 cursor-pointer transition-colors`}

    .special-insert-title-icon-wrapper {
      ${tw`flex items-center gap-x-2.5`}
    }

    .special-insert-item-plus-icon {
      ${tw`px-1.5`}

      svg {
        ${tw`w-5 h-5`}
        path {
          stroke: ${textColors['neutral-moderate']};
        }
      }
    }

    .special-insert-icon {
      ${tw`rounded-lg h-8 w-8 inline-flex items-center justify-center border`}
      border-color: ${borderColors['neutral-quiet']};
    }

    &:hover {
      background-color: ${backgroundColors['neutral-supershy']};
    }
  }

  .special-insert-color-box {
    ${tw`flex-shrink-0 rounded flex items-center justify-center w-[30px] h-[30px] relative`}
  }

  .special-insert-icon {
    ${tw`flex items-center justify-center w-6 h-6`}

    svg {
      ${tw`w-full h-full`}
    }
  }

  .special-insert-label {
    ${tw`text-base font-semibold flex-1`}
    color: ${textColors['neutral-moderate']};
  }

  /* Froala popup adjustments for special inserts */
  .fr-popup.fr-active[data-id='specialInserts.popup'] {
    ${tw`p-0! rounded-lg! shadow-lg! mt-2! border!`}
    border-color: ${borderColors['neutral-quiet']} !important;
  }

  .fr-popup.fr-active[data-id='specialInserts.popup'] .fr-arrow {
    ${tw`hidden!`}
  }

  .special-inserts-dropdown {
    ${tw`overflow-y-auto! max-h-[200px]!`}
  }
`;
